import React from 'react';
import * as PropTypes from 'prop-types';
import { Icon } from '@ant-design/compatible';
import MemberDropdown from './MemberDropdown';
import RemoveButtonGroup from './RemoveButtonGroup';

// Strip view/cube name from title so only field name is shown
const fieldTitleOnly = (memberTitle, cube) => {
  if (!memberTitle) return memberTitle;
  const viewLabel = cube?.title || (cube?.name || '').replace(/_/g, ' ');
  if (!viewLabel) return memberTitle;
  const prefix = viewLabel.trim() + ' ';
  return memberTitle.startsWith(prefix) ? memberTitle.slice(prefix.length) : memberTitle;
};

// Helper function to get display name from member
const getDisplayName = (member, availableMembers, meta) => {
  // Try to find in availableMembers first (most reliable; uses displayTitle = field name only)
  if (availableMembers && member.name) {
    const found = availableMembers.find(m => m.name === member.name);
    if (found) {
      return found.displayTitle ?? found.title ?? found.name;
    }
  }
  
  // Fallback to metadata lookup (for field IDs like "cube.field")
  if (meta && meta.cubes && member.name) {
    const parts = member.name.split('.');
    if (parts.length === 2) {
      const [cubeName, fieldName] = parts;
      const cube = meta.cubes.find(c => c.name === cubeName);
      if (cube) {
        const measure = cube.measures?.find(m => m.name === fieldName);
        if (measure) {
          const t = measure.title ? fieldTitleOnly(measure.title, cube) : null;
          if (t) return t;
        }
        const dimension = cube.dimensions?.find(d => d.name === fieldName);
        if (dimension) {
          const t = dimension.title ? fieldTitleOnly(dimension.title, cube) : null;
          if (t) return t;
        }
      }
    }
  }

  // Strip view name from member.title if present (e.g. "Transactions view Transactionid" -> "Transactionid")
  if (member.title && meta?.cubes && member.name) {
    const parts = member.name.split('.');
    if (parts.length === 2) {
      const cube = meta.cubes.find(c => c.name === parts[0]);
      if (cube) return fieldTitleOnly(member.title, cube) || member.title;
    }
  }
  
  // Final fallback
  return member.title || member.name;
};

const MemberGroup = ({
  members,
  availableMembers,
  addMemberName,
  updateMethods,
  meta,
}) => {
  // Don't show already-selected members in the dropdown
  const selectedNames = members.map((m) => m.name);
  const availableToSelect = availableMembers.filter(
    (m) => !selectedNames.includes(m.name)
  );

  return (
    <span>
      {members.map((m) => (
        <RemoveButtonGroup
          key={m.index || m.name}
          onRemoveClick={() => updateMethods.remove(m)}
        >
          <MemberDropdown
            availableMembers={availableToSelect}
            onClick={(updateWith) => updateMethods.update(m, updateWith)}
          >
            {getDisplayName(m, availableMembers, meta)}
          </MemberDropdown>
        </RemoveButtonGroup>
      ))}
      <MemberDropdown
        onClick={(m) => updateMethods.add(m)}
        availableMembers={availableToSelect}
        type="dashed"
        icon={<Icon type="plus" />}
      >
        {addMemberName}
      </MemberDropdown>
    </span>
  );
};
MemberGroup.propTypes = {
  members: PropTypes.array.isRequired,
  availableMembers: PropTypes.array.isRequired,
  addMemberName: PropTypes.string.isRequired,
  updateMethods: PropTypes.object.isRequired,
};
export default MemberGroup;
